/* @flow */

import { sort } from 'ramda';
import { getBraindateId } from '@braindate/domain/lib/braindate/util';
import { assert, assertObject, assertArray } from '@braindate/util/lib/assert';
import { isString } from '@braindate/util/lib/type';

import {
  notificationErrorLevel,
  notificationSuccessLevel,
  notificationInfoLevel,
  notificationWarningLevel,
  notificationTakeOverType,
  notificationTopType,
  notificationModalType,
} from 'src/shared/app/notification/constant/notificationConstants';
import VirtualBraindateEarlyReminder from 'plugin/virtual-braindate/component/notification/VirtualBraindateEarlyReminder';
import { braindateRoute } from 'src/shared/app/base/route/setting/routeSettings';
import { getRoutePath } from 'src/shared/app/base/route/util/routeUtils';

import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { RouteData } from 'src/shared/app/base/route/type/routeTypes';
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';
import { NOTIFICATION_COMPONENT_MAPPGIN } from 'src/shared/app/notification/constant/notificationComponentsMap';

// DIRTY
export function mapNotificationComponentClass(comp: any): any {
  if (isString(comp)) {
    switch (comp) {
      case 'VirtualBraindateEarlyReminder':
        return VirtualBraindateEarlyReminder;
    }
  }

  return comp;
}

// DIRTY, Yes it is
export function mapNotificationPath(
  route: any,
  notification: Notification<>,
  state: any,
): string {
  if (isString(route)) {
    if ('braindateRoute' === route) {
      return getRoutePath(braindateRoute, state);
    }

    if ('braindateRoute:id' === route) {
      // $FlowIssue
      const braindate: Braindate = getNotificationData(notification);
      const braindateId = getBraindateId(braindate);
      const braindatePath = getRoutePath(braindateRoute, state);

      return braindatePath.replace(':id', String(braindateId));
    }
  }

  return getRoutePath(route, state);
}

/*
|------------------------------------------------------------------------------
| GETTERS
|------------------------------------------------------------------------------
*/

export function getNotificationId(notification: Notification<>): string {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.id;
}

export function getNotificationLevel(notification: Notification<>): string {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.level;
}

export function getNotificationType(notification: Notification<>): string {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.type;
}

export function getNotificationComponentClass(
  notification: Notification<>,
): any {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  // @TODO componentClass will become deprecated because we need to prevent
  // from adding React Component in Redux state.
  const { componentKey, componentClass } = notification;
  if (componentKey) {
    const Component = NOTIFICATION_COMPONENT_MAPPGIN[componentKey];
    if (Component) {
      return Component;
    }
  }

  return componentClass;
}

export function getNotificationData(notification: Notification<>): ?Object {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.data;
}

export function getNotificationExcludePaths(
  notification: Notification<>,
): Array<string | RouteData> {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  const { exclude } = notification;

  return Array.isArray(exclude) ? exclude : [];
}

export function getNotificationIncludePaths(
  notification: Notification<>,
): Array<any> {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  const { include } = notification;

  return Array.isArray(include) ? include : [];
}

export function getNotificationIsActive(notification: Notification<>): boolean {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.active || false;
}

export function getNotificationBundleType(
  notification: Notification<>,
): ?string {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.bundleType;
}

/*
|------------------------------------------------------------------------------
| CHECKERS
|------------------------------------------------------------------------------
*/

export function isTakeOverNotification(notification: Notification<>): boolean {
  return getNotificationType(notification) === notificationTakeOverType;
}

export function isVideoCallTopNotification(
  notification: Notification<>,
): boolean {
  return getNotificationType(notification) === notificationTopType;
}

export function isModalNotification(notification: Notification<>): boolean {
  return getNotificationType(notification) === notificationModalType;
}

/*
|------------------------------------------------------------------------------
| FILTERS
|------------------------------------------------------------------------------
*/

export function getErrorNotifications(
  notifications: Array<Notification<>>,
): Array<Notification<>> {
  assertArray(notifications, 'notifications');

  return notifications.filter(
    (n) => getNotificationLevel(n) === notificationErrorLevel,
  );
}

export function getSuccessNotifications(
  notifications: Array<Notification<>>,
): Array<Notification<>> {
  assertArray(notifications, 'notifications');

  return notifications.filter(
    (n) => getNotificationLevel(n) === notificationSuccessLevel,
  );
}

export function getInfoNotifications(
  notifications: Array<Notification<>>,
): Array<Notification<>> {
  assertArray(notifications, 'notifications');

  return notifications.filter(
    (n) => getNotificationLevel(n) === notificationInfoLevel,
  );
}

export function getWarningNotifications(
  notifications: Array<Notification<>>,
): Array<Notification<>> {
  assertArray(notifications, 'notifications');

  return notifications.filter(
    (n) => getNotificationLevel(n) === notificationWarningLevel,
  );
}

export function getActiveNotifications(
  notifications: Array<Notification<>>,
): Array<Notification<>> {
  assertArray(notifications, 'notifications');

  return notifications.filter((n) => getNotificationIsActive(n));
}

export function getInactiveNotifications(
  notifications: Array<Notification<>>,
): Array<Notification<>> {
  assertArray(notifications, 'notifications');

  return notifications.filter((n) => !getNotificationIsActive(n));
}

/*
|------------------------------------------------------------------------------
| SORTING
|------------------------------------------------------------------------------
*/

export function orderNotificationsByPriority(
  notifications: Array<Notification<>>,
): Array<Notification<>> {
  assertArray(notifications, 'notifications');

  const order = [
    notificationErrorLevel,
    notificationSuccessLevel,
    notificationInfoLevel,
    notificationWarningLevel,
  ];

  return sort((a, b) => {
    const aScore = order.indexOf(getNotificationLevel(a));
    const bScore = order.indexOf(getNotificationLevel(b));

    return aScore - bScore;
  }, notifications);
}
